import { apiRequest } from './api';

export type TimezoneOption = {
  id: number;
  name: string;
  /** IANA zone e.g. Asia/Kolkata — field name may vary by BE */
  code?: string | null;
  value?: string | null;
  timezone?: string | null;
  is_active?: boolean;
};

export type LanguageOption = {
  id: number;
  name: string;
  code?: string | null;
  is_active?: boolean;
};

export type GeneralSetting = {
  organization_name: string | null;
  admin_email: string | null;
  timezone_id: number | null;
  timezone?: TimezoneOption | null;
};

export type UpdateGeneralSettingPayload = {
  organization_name?: string;
  admin_email?: string;
  timezone_id: number;
};

const DEFAULT_TIMEZONE_HINT = 'asia/kolkata';

export function timezoneCode(tz: TimezoneOption): string {
  return (tz.code || tz.value || tz.timezone || tz.name || '').trim();
}

/** Pick Asia/Kolkata (or first active) when user has no timezone yet. */
export function resolveDefaultTimezoneId(
  timezones: TimezoneOption[],
  currentId?: number | null
): number | null {
  if (currentId != null) {
    const match = timezones.find((t) => t.id === currentId);
    if (match) return match.id;
  }
  const byKolkata = timezones.find((t) =>
    timezoneCode(t).toLowerCase().includes(DEFAULT_TIMEZONE_HINT)
  );
  if (byKolkata) return byKolkata.id;
  return timezones[0]?.id ?? null;
}

/**
 * GET /api/general-setting
 * Returns general settings for the logged-in user (stored on user table).
 */
export async function fetchGeneralSetting(): Promise<GeneralSetting> {
  const result = await apiRequest<GeneralSetting>('/api/general-setting');
  const data = result.data;
  return {
    organization_name: data?.organization_name ?? null,
    admin_email: data?.admin_email ?? null,
    timezone_id: data?.timezone_id ?? null,
    timezone: data?.timezone ?? null,
  };
}

/**
 * PUT /api/general-setting
 * Saves general settings for the logged-in user.
 */
export async function updateGeneralSetting(
  payload: UpdateGeneralSettingPayload
): Promise<GeneralSetting> {
  const result = await apiRequest<GeneralSetting>('/api/general-setting', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = result.data;
  return {
    organization_name: data?.organization_name ?? payload.organization_name ?? null,
    admin_email: data?.admin_email ?? payload.admin_email ?? null,
    timezone_id: data?.timezone_id ?? payload.timezone_id,
    timezone: data?.timezone ?? null,
  };
}

/** GET /api/timezones — master list (already on BE). */
export async function fetchTimezones(): Promise<TimezoneOption[]> {
  const result = await apiRequest<{ timezones: TimezoneOption[] } | TimezoneOption[]>(
    '/api/timezones'
  );
  const data = result.data;
  if (Array.isArray(data)) return data;
  return data?.timezones || [];
}

export type SecuritySetting = {
  otp_required_for_book_access: boolean;
  two_factor_enabled: boolean;
  otp_expiry_minutes: number;
  session_timeout_minutes: number;
};

export type UpdateSecuritySettingPayload = SecuritySetting;

/**
 * GET /api/security-setting
 * Returns security settings for the logged-in user (stored on user table).
 */
export async function fetchSecuritySetting(): Promise<SecuritySetting> {
  const result = await apiRequest<SecuritySetting>('/api/security-setting');
  const data = result.data;
  return {
    otp_required_for_book_access: Boolean(
      data?.otp_required_for_book_access ?? true
    ),
    two_factor_enabled: Boolean(data?.two_factor_enabled ?? false),
    otp_expiry_minutes: Number(data?.otp_expiry_minutes ?? 30),
    session_timeout_minutes: Number(data?.session_timeout_minutes ?? 60),
  };
}

/**
 * PUT /api/security-setting
 * Saves security settings for the logged-in user.
 */
export async function updateSecuritySetting(
  payload: UpdateSecuritySettingPayload
): Promise<SecuritySetting> {
  const result = await apiRequest<SecuritySetting>('/api/security-setting', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = result.data;
  return {
    otp_required_for_book_access: Boolean(
      data?.otp_required_for_book_access ?? payload.otp_required_for_book_access
    ),
    two_factor_enabled: Boolean(
      data?.two_factor_enabled ?? payload.two_factor_enabled
    ),
    otp_expiry_minutes: Number(
      data?.otp_expiry_minutes ?? payload.otp_expiry_minutes
    ),
    session_timeout_minutes: Number(
      data?.session_timeout_minutes ?? payload.session_timeout_minutes
    ),
  };
}

export type NotificationItem = {
  enabled: boolean;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
};

export type NotificationSetting = {
  book_upload: NotificationItem;
  book_expiry: NotificationItem;
  otp_generation: NotificationItem;
  login_2fa: NotificationItem;
};

export type UpdateNotificationSettingPayload = NotificationSetting;

export const DEFAULT_NOTIFICATION_SETTING: NotificationSetting = {
  book_upload: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'New Book Uploaded — {{book_title}}',
    body: 'Hello {{user_name}},\n\nA new book "{{book_title}}" has been uploaded to the library.\n\nPlease log in to the admin portal to review it.\n\nRegards,\nFlipbook Admin',
  },
  book_expiry: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Book Expiry Alert — {{book_title}}',
    body: 'Hello {{user_name}},\n\nThe book "{{book_title}}" is set to expire on {{expiry_date}}.\n\nPlease renew it to ensure continued access for students.\n\nRegards,\nFlipbook Admin',
  },
  otp_generation: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Your OTP for Flipbook Access',
    body: 'Hello {{user_name}},\n\nYour one-time password (OTP) to access "{{book_title}}" is:\n\n{{otp_code}}\n\nThis OTP is valid for {{otp_expiry_minutes}} minutes. Do not share it with anyone.\n\nRegards,\nFlipbook Admin',
  },
  login_2fa: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Your Flipbook login verification code',
    body: 'Hello {{user_name}},\n\nUse this one-time password to finish signing in to Flipbook:\n\n{{otp_code}}\n\nThis code expires in {{otp_expiry_minutes}} minutes.\n\nIf you did not try to sign in, change your password and contact an administrator.\n\nRegards,\nFlipbook Admin',
  },
};

function normalizeNotificationItem(
  item: Partial<NotificationItem> | null | undefined,
  fallback: NotificationItem
): NotificationItem {
  return {
    enabled: Boolean(item?.enabled ?? fallback.enabled),
    to: typeof item?.to === 'string' ? item.to : fallback.to,
    cc: typeof item?.cc === 'string' ? item.cc : fallback.cc,
    bcc: typeof item?.bcc === 'string' ? item.bcc : fallback.bcc,
    subject: typeof item?.subject === 'string' ? item.subject : fallback.subject,
    body: typeof item?.body === 'string' ? item.body : fallback.body,
  };
}

function normalizeNotificationSetting(
  data?: Partial<NotificationSetting> | null
): NotificationSetting {
  return {
    book_upload: normalizeNotificationItem(
      data?.book_upload,
      DEFAULT_NOTIFICATION_SETTING.book_upload
    ),
    book_expiry: normalizeNotificationItem(
      data?.book_expiry,
      DEFAULT_NOTIFICATION_SETTING.book_expiry
    ),
    otp_generation: normalizeNotificationItem(
      data?.otp_generation,
      DEFAULT_NOTIFICATION_SETTING.otp_generation
    ),
    login_2fa: normalizeNotificationItem(
      data?.login_2fa,
      DEFAULT_NOTIFICATION_SETTING.login_2fa
    ),
  };
}

/**
 * GET /api/notification-setting
 * Returns notification preferences for the logged-in user.
 */
export async function fetchNotificationSetting(): Promise<NotificationSetting> {
  const result = await apiRequest<NotificationSetting>('/api/notification-setting');
  return normalizeNotificationSetting(result.data);
}

/**
 * PUT /api/notification-setting
 * Saves notification preferences for the logged-in user.
 */
export async function updateNotificationSetting(
  payload: UpdateNotificationSettingPayload
): Promise<NotificationSetting> {
  const result = await apiRequest<NotificationSetting>('/api/notification-setting', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeNotificationSetting(result.data ?? payload);
}

export type EmailTemplateItem = {
  enabled: boolean;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
};

export type EmailSetting = {
  publish_approval_request: EmailTemplateItem;
  publish_approval_reminder: EmailTemplateItem;
  delete_approval_request: EmailTemplateItem;
  delete_approval_reminder: EmailTemplateItem;
};

export type UpdateEmailSettingPayload = Partial<EmailSetting>;

export const DEFAULT_EMAIL_SETTING: EmailSetting = {
  publish_approval_request: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Publish Approval Required — {{book_title}}',
    body: 'Hello {{approver_name}},\n\nA new book "{{book_title}}" has been submitted by {{requester_name}} and needs your approval for publishing.\n\nPlease approve or reject this request. If you are not signed in, you will be asked to log in first and then taken to the approval page.\n\n{{approval_link}}\n\nRegards,\nFlipbook Admin',
  },
  publish_approval_reminder: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Reminder — Publish approval for "{{book_title}}" is still pending',
    body: 'Hello {{approver_name}},\n\nThis is a reminder that publishing for "{{book_title}}" submitted by {{requester_name}} is still pending your approval.\n\nPlease approve or reject this request. If you are not signed in, you will be asked to log in first and then taken to the approval page.\n\n{{approval_link}}\n\nRegards,\nFlipbook Admin',
  },
  delete_approval_request: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Delete Approval Required — {{book_title}}',
    body: 'Hello {{approver_name}},\n\nA deletion request for "{{book_title}}" has been submitted by {{requester_name}} and needs your approval.\n\nPlease review and approve or reject this deletion request. If you are not signed in, you will be asked to log in first and then taken to the approval page.\n\n{{approval_link}}\n\nRegards,\nFlipbook Admin',
  },
  delete_approval_reminder: {
    enabled: true,
    to: '',
    cc: '',
    bcc: '',
    subject: 'Reminder — Delete approval for "{{book_title}}" is still pending',
    body: 'Hello {{approver_name}},\n\nThis is a reminder that the deletion request for "{{book_title}}" submitted by {{requester_name}} is still pending your approval.\n\nPlease review and approve or reject this request. If you are not signed in, you will be asked to log in first and then taken to the approval page.\n\n{{approval_link}}\n\nRegards,\nFlipbook Admin',
  },
};

function normalizeEmailTemplateItem(
  item: Partial<EmailTemplateItem> | null | undefined,
  fallback: EmailTemplateItem
): EmailTemplateItem {
  return {
    enabled: Boolean(item?.enabled ?? fallback.enabled),
    to: typeof item?.to === 'string' ? item.to : fallback.to,
    cc: typeof item?.cc === 'string' ? item.cc : fallback.cc,
    bcc: typeof item?.bcc === 'string' ? item.bcc : fallback.bcc,
    subject: typeof item?.subject === 'string' ? item.subject : fallback.subject,
    body: typeof item?.body === 'string' ? item.body : fallback.body,
  };
}

function normalizeEmailSetting(
  data?: Partial<EmailSetting & { approval_request?: EmailTemplateItem; approval_reminder?: EmailTemplateItem }> | null
): EmailSetting {
  return {
    publish_approval_request: normalizeEmailTemplateItem(
      data?.publish_approval_request ?? data?.approval_request,
      DEFAULT_EMAIL_SETTING.publish_approval_request
    ),
    publish_approval_reminder: normalizeEmailTemplateItem(
      data?.publish_approval_reminder ?? data?.approval_reminder,
      DEFAULT_EMAIL_SETTING.publish_approval_reminder
    ),
    delete_approval_request: normalizeEmailTemplateItem(
      data?.delete_approval_request ?? data?.approval_request,
      DEFAULT_EMAIL_SETTING.delete_approval_request
    ),
    delete_approval_reminder: normalizeEmailTemplateItem(
      data?.delete_approval_reminder ?? data?.approval_reminder,
      DEFAULT_EMAIL_SETTING.delete_approval_reminder
    ),
  };
}

/**
 * GET /api/email-setting
 * Org-wide approval email templates (Settings → Email).
 */
export async function fetchEmailSetting(): Promise<EmailSetting> {
  const result = await apiRequest<EmailSetting>('/api/email-setting');
  return normalizeEmailSetting(result.data);
}

/**
 * PUT /api/email-setting
 * Save approval email templates.
 */
export async function updateEmailSetting(
  payload: UpdateEmailSettingPayload
): Promise<EmailSetting> {
  const result = await apiRequest<EmailSetting>('/api/email-setting', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return normalizeEmailSetting(result.data ?? payload);
}

export type FlipbookSetting = {
  watermark_enabled: boolean;
  watermark_image_url?: string | null;
  allow_pdf_download: boolean;
  default_book_expiry_days: number;
  max_pages_per_book: number;
  max_size_per_book_mb: number;
};

export type UpdateFlipbookSettingPayload = {
  watermark_enabled: boolean;
  allow_pdf_download: boolean;
  default_book_expiry_days: number;
  max_pages_per_book: number;
  max_size_per_book_mb: number;
  watermark_image_url?: string | null;
  watermark_image?: File | null;
  remove_watermark_image?: boolean;
};

/**
 * GET /api/flipbook-setting
 * Returns flipbook settings for the logged-in user.
 */
export async function fetchFlipbookSetting(): Promise<FlipbookSetting> {
  const result = await apiRequest<
    FlipbookSetting & { watermark_image?: string | null }
  >('/api/flipbook-setting');
  const data = result.data;
  return {
    watermark_enabled: Boolean(data?.watermark_enabled ?? true),
    watermark_image_url:
      data?.watermark_image_url ?? data?.watermark_image ?? null,
    allow_pdf_download: Boolean(data?.allow_pdf_download ?? false),
    default_book_expiry_days: Number(data?.default_book_expiry_days ?? 180),
    max_pages_per_book: Number(data?.max_pages_per_book ?? 500),
    max_size_per_book_mb: Number(data?.max_size_per_book_mb ?? 1024),
  };
}

/**
 * PUT /api/flipbook-setting
 * Saves flipbook settings for the logged-in user.
 * Supports multipart/form-data when a watermark image file is uploaded.
 */
export async function updateFlipbookSetting(
  payload: UpdateFlipbookSettingPayload
): Promise<FlipbookSetting> {
  let body: FormData | string;

  if (payload.watermark_image || payload.remove_watermark_image) {
    const formData = new FormData();
    formData.append('watermark_enabled', String(payload.watermark_enabled));
    formData.append('allow_pdf_download', String(payload.allow_pdf_download));
    formData.append(
      'default_book_expiry_days',
      String(payload.default_book_expiry_days)
    );
    formData.append(
      'max_pages_per_book',
      String(payload.max_pages_per_book)
    );
    formData.append(
      'max_size_per_book_mb',
      String(payload.max_size_per_book_mb)
    );
    if (payload.watermark_image) {
      formData.append('watermark_image', payload.watermark_image);
    }
    if (payload.remove_watermark_image) {
      formData.append('remove_watermark_image', 'true');
    }
    body = formData;
  } else {
    body = JSON.stringify({
      watermark_enabled: payload.watermark_enabled,
      allow_pdf_download: payload.allow_pdf_download,
      default_book_expiry_days: payload.default_book_expiry_days,
      max_pages_per_book: payload.max_pages_per_book,
      max_size_per_book_mb: payload.max_size_per_book_mb,
      ...(payload.watermark_image_url !== undefined
        ? { watermark_image_url: payload.watermark_image_url }
        : {}),
    });
  }

  const result = await apiRequest<
    FlipbookSetting & { watermark_image?: string | null }
  >('/api/flipbook-setting', {
    method: 'PUT',
    body,
  });
  const data = result.data;
  return {
    watermark_enabled: Boolean(
      data?.watermark_enabled ?? payload.watermark_enabled
    ),
    watermark_image_url:
      data?.watermark_image_url ??
      data?.watermark_image ??
      (payload.remove_watermark_image
        ? null
        : payload.watermark_image_url ?? null),
    allow_pdf_download: Boolean(
      data?.allow_pdf_download ?? payload.allow_pdf_download
    ),
    default_book_expiry_days: Number(
      data?.default_book_expiry_days ?? payload.default_book_expiry_days
    ),
    max_pages_per_book: Number(
      data?.max_pages_per_book ?? payload.max_pages_per_book
    ),
    max_size_per_book_mb: Number(
      data?.max_size_per_book_mb ?? payload.max_size_per_book_mb
    ),
  };
}

